// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { DealerStateContext } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { DealerDetailsPageContext } from "@insite/content-library/Pages/DealerDetailsPage";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadUpcomingTrainingAndEvents from "@mcmc/App/Handlers/UpcomingTrainingAndEvents/LoadUpcomingTrainingAndEvents";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getUpcomingTrainingAndEvents } from "@mcmc/App/Store/Selectors/UpcomingTrainingAndEventsSelectors";
import { createSectionTitleWidget } from "@mcmc/App/Widgets/SectionTitleWidget/SectionTitleWidget";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState) => ({
    events: getUpcomingTrainingAndEvents(state)?.eventsCollection,
});

const mapDispatchToProps = {
    loadUpcomingTrainingAndEvents,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface UpcomingTrainingAndEventsTableStyles {
    mainWrapper?: InjectableCss;
    dataTableWrapper?: InjectableCss;
}

export const upcomingTrainingAndEventsTableStyles: UpcomingTrainingAndEventsTableStyles = {
    mainWrapper: {
        css: css`
            ${styledBox};
            margin-top: 10px;
            margin-bottom: 0px;
            .title-slash {
                display: none;
            }
        `,
    },
    dataTableWrapper: {
        css: css`
            overflow-x: auto;
            position: relative;
            & th {
                min-width: 100px;
            }
        `,
    },
};

const styles = upcomingTrainingAndEventsTableStyles;

const UpcomingTrainingAndEventsTable: React.FC<Props> = ({ loadUpcomingTrainingAndEvents, events }) => {
    const { value: dealer } = React.useContext(DealerStateContext);

    React.useEffect(() => {
        if (dealer) {
            loadUpcomingTrainingAndEvents({
                state: dealer.state,
                city: dealer.city,
            });
        }
    }, [dealer]);

    if (!dealer || !events || events.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.mainWrapper}>
            {createSectionTitleWidget(translate("Upcoming Training & Events"))}
            <StyledWrapper {...styles.dataTableWrapper}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader className="no-sticky">{translate("Date")}</DataTableHeader>
                        <DataTableHeader>{translate("Topic")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {events.map(({ id, shortDescription, startDate, urlSegment }) => (
                            <DataTableRow key={id}>
                                <DataTableCell className="no-sticky">
                                    <LocalizedDateTime
                                        dateTime={startDate}
                                        options={{ day: "2-digit", month: "2-digit", year: "2-digit" }}
                                    />
                                </DataTableCell>
                                <DataTableCell>
                                    <Link href={urlSegment}>{shortDescription}</Link>
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const upcomingTrainingAndEventsTableWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UpcomingTrainingAndEventsTable),
    definition: {
        group: "Dealer Details",
        allowedContexts: [DealerDetailsPageContext],
    },
};

export default upcomingTrainingAndEventsTableWidget;
